<script setup lang="ts">
// the file on disk uses NFD-decomposed diacritics (š/ć as base letter + combining
// mark); normalize to match, otherwise the encoded URL won't resolve to the file
const courseTermsUrl = encodeURI(
	"/Uslovi korišćenja online kursa programiranja.pdf".normalize("NFD"),
);
const privacyPolicyUrl = encodeURI("/POLITIKA PRIVATNOSTI PRAKSA .pdf");

const form = defineModel<{
	questions: string;
	experience: string;
	build: string;
	rules: boolean;
	self_withdrawl: boolean;
	no_outside_help: boolean;
	i_understand_course: boolean;
}>();

const maxLengthTextarea = 600;
watch(
	() => form.value?.questions,
	(val, old_val) => {
		if (!val) {
			return;
		}
		if (form.value && val.length > maxLengthTextarea) {
			form.value.questions = old_val || "";
		}
	},
);
watch(
	() => form.value?.build,
	(val, old_val) => {
		if (!val) {
			return;
		}
		if (form.value && val.length > maxLengthTextarea) {
			form.value.build = old_val || "";
		}
	},
);
function validate(field: keyof typeof touched) {
	touched[field] = true;
}

function onFocus(field: keyof typeof touched) {
	touched[field] = false;
}

function onBlur(field: keyof typeof touched) {
	validate(field);
}
const questionCounter = computed(() => {
	if (!form.value) {
		return `0/${maxLengthTextarea}`;
	}
	return `${form.value.questions ? form.value.questions.length : 0}/${maxLengthTextarea}`;
});
const buildCounter = computed(() => {
	if (!form.value) {
		return `0/${maxLengthTextarea}`;
	}
	return `${form.value.build ? form.value.build.length : 0}/${maxLengthTextarea}`;
});

const touched = reactive({
	questions: false,
	experience: false,
	build: false,
});

const errors = computed(() => ({
	questions: touched.questions && !form.value?.questions?.trim(),

	experience:
		touched.experience &&
		(form.value?.experience === null ||
			form.value?.experience === undefined ||
			form.value?.experience === ""),

	build: touched.build && !form.value?.build?.trim(),
}));

function validateAll() {
	touched.questions = true;
	touched.experience = true;
	touched.build = true;
}

const isValid = computed(() => {
	return (
		!!form.value?.questions?.trim() &&
		!!form.value?.build?.trim() &&
		form.value?.experience !== null &&
		form.value?.experience !== undefined &&
		form.value?.experience !== ""
	);
});

defineExpose({ validateAll, isValid });
</script>

<template>
	<section class="section">
		<h2>A few questions</h2>

		<div class="questions">
			<div v-if="form" class="field">
				<label class="field__label">
					Why do you want to join this course? *
				</label>

				<textarea
					v-model="form.questions"
					class="field__textarea"
					:class="{ error: errors.questions }"
					@focus="onFocus('questions')"
					@blur="onBlur('questions')"
				/>

				<span v-if="errors.questions" class="field__error">
					This field is mandatory
				</span>

				<span class="textarea-counter" :class="{ error: errors.questions }">
					{{ questionCounter }} characters
				</span>
			</div>

			<div v-if="form" class="field">
				<label class="field__label">
					How would you describe your current level of experience with frontend
					development? *
				</label>
				<div class="field__select-wrapper">
					<select
						v-model="form.experience"
						class="field__select"
						:class="{ error: errors.experience }"
						@focus="onFocus('experience')"
						@blur="onBlur('experience')"
					>
						<option value="">Choose an option</option>
						<option value="0">Beginner</option>
						<option value="1">Junior</option>
						<option value="2">Intermediate</option>
						<option value="3">Advanced</option>
						<option value="4">Senior</option>
					</select>
				</div>
				<span v-if="errors.experience" class="field__error">
					This field is mandatory
				</span>
			</div>

			<div v-if="form" class="field">
				<label class="field__label">
					Is there anything specific you're hoping to learn or build? *
				</label>

				<textarea
					v-model="form.build"
					class="field__textarea"
					:class="{ error: errors.build }"
					@focus="onFocus('build')"
					@blur="onBlur('build')"
				/>

				<span v-if="errors.build" class="field__error">
					This field is mandatory
				</span>

				<span class="textarea-counter" :class="{ error: errors.build }">
					{{ buildCounter }} characters
				</span>
			</div>

			<div class="checkboxes">
				<label v-if="form" class="checkbox">
					<input v-model="form.rules" type="checkbox" />
					<span class="checkbox__box"></span>
					<span class="checkbox__label">
						I completed the task independently and the work submitted is my own
					</span>
				</label>

				<label v-if="form" class="checkbox">
					<input v-model="form.self_withdrawl" type="checkbox" />
					<span class="checkbox__box"></span>
					<span class="checkbox__label">
						I have read, understood and accepted the
						<a
							:href="courseTermsUrl"
							target="_blank"
							rel="noopener noreferrer"
							class="checkbox__link"
							@click.stop
						>
							application rules
						</a>
						for the Frontend Development Course and the
						<a
							:href="privacyPolicyUrl"
							target="_blank"
							rel="noopener noreferrer"
							class="checkbox__link"
							@click.stop
						>
							Privacy policy
						</a>
					</span>
				</label>
				<label v-if="form" class="checkbox">
					<input v-model="form.i_understand_course" type="checkbox" />
					<span class="checkbox__box"></span>
					<span class="checkbox__label">
						I understand that the course is conducted online and confirm that I
						have a stable internet connection, as well as access to a computer.
					</span>
				</label>

				<label v-if="form" class="checkbox">
					<input v-model="form.no_outside_help" type="checkbox" />
					<span class="checkbox__box"></span>
					<span class="checkbox__label">
						I have not read any of the above statements and would like to
						withdraw my application
					</span>
				</label>
			</div>
		</div>
	</section>
</template>

<style lang="scss" scoped>
@use "@/assets/css/vars" as *;

$error-color-internship: #d92d20;
.section {
	display: flex;
	flex-direction: column;
	align-items: center;
	max-width: 1100px;
	margin: 0 auto;
	width: 100%;
	gap: 40px;

	h2 {
		font-family: $font-heading;
		font-size: $text-2xl;
		font-weight: $font-bold;
		line-height: 46px;
		letter-spacing: -1px;
		color: $text-color;
		align-self: flex-start;
	}
}

.questions {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 64px;
	width: 100%;
}

.field {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 20px;
	position: relative;
	width: 100%;
	&__error {
		font-family: $font-main;
		font-weight: $font-regular;
		font-size: $text-sm;
		line-height: 150%;
		color: #d92d20;
	}
	&__label {
		font-family: $font-main;
		font-weight: $font-regular;
		font-size: $text-xl;
		line-height: 130%;
		color: #333;
	}

	&__textarea {
		box-sizing: border-box;
		width: 100%;
		height: 194px;
		padding: 16px;
		border: 1px solid #acacac !important;
		border-radius: 12px;
		background: $background-color-f5;
		outline: none;
		resize: none;
		font-family: $font-main;
		font-weight: $font-regular;
		font-size: $text-lg;
		line-height: 130%;
		color: $text-color;
		transition:
			border-color 0.2s ease,
			box-shadow 0.2s ease;

		&::placeholder {
			color: $text-placeholder;
		}

		&:focus {
			border-color: $second-app-color !important;
			box-shadow: 0 0 0 4px rgba(88, 125, 168, 0.3);
		}

		&.error {
			border-color: $error-color-internship !important;

			&:focus {
				border-color: $error-color-internship !important;
				box-shadow: 0 0 0 4px rgba(217, 45, 32, 0.3);
			}
		}
	}
	.textarea-counter {
		margin-top: -8px;
		color: #808080;
		transition: color 0.2s ease;

		&.error {
			color: $error-color-internship;
		}
	}

	&__select-wrapper {
		width: 100%;
	}

	&__select {
		box-sizing: border-box;
		width: 100%;
		height: 58px;
		padding: 16px;
		border: 1px solid #acacac;
		border-radius: 12px;
		background: $background-color-f5;
		outline: none;
		appearance: none;
		font-family: $font-main;
		font-weight: $font-regular;
		font-size: $text-lg;
		line-height: 130%;
		color: $text-placeholder;
		cursor: pointer;
		transition:
			border-color 0.2s ease,
			box-shadow 0.2s ease;
		background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M6 9l6 6 6-6' stroke='%23787878' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
		background-repeat: no-repeat;
		background-position: right 16px center;

		&:focus {
			border-color: $second-app-color;
			box-shadow: 0 0 0 4px rgba(88, 125, 168, 0.3);
		}

		// error state
		&.error {
			border-color: #d92d20;

			// ako je fokusiran i u error stanju
			&:focus {
				border-color: #d92d20;
				box-shadow: 0 0 0 4px rgba(217, 45, 32, 0.3);
			}
		}

		option {
			color: $text-color;
		}
	}
}

.checkboxes {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 16px;
	width: 100%;
	max-width: 609px;
}

.checkbox {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 9px;
	cursor: pointer;
	&--error {
		border-color: #d92d20;
	}
	input[type="checkbox"] {
		position: absolute;
		opacity: 0;
		width: 22px;
		height: 22px;
		margin: 0;
		cursor: pointer;

		&:checked + .checkbox__box {
			background: #4ebe7c;
			border-color: $app-color;

			&::after {
				opacity: 1;
			}
		}
	}

	&__box {
		box-sizing: border-box;
		flex: none;
		width: 22px;
		height: 22px;
		border: 2px solid #acacac;
		border-radius: 6px;
		background: $background-color-f5;
		transition:
			background 0.2s ease,
			border-color 0.2s ease;
		position: relative;

		&::after {
			content: "";
			position: absolute;
			left: 50%;
			top: 45%;
			width: 5px;
			height: 9px;
			border: solid $text-white;
			border-width: 0 2px 2px 0;
			transform: translate(-50%, -50%) rotate(45deg);
			opacity: 0;
			transition: opacity 0.2s ease;
		}
	}

	&__label {
		font-family: $font-main;
		font-weight: $font-regular;
		font-size: $text-sm;
		line-height: 150%;
		color: #4b5563;
	}

	&__link {
		color: $app-color;
		text-decoration: underline;
	}
}

@media screen and (max-width: 768px) {
	.section {
		gap: 24px;

		h2 {
			font-size: $text-xl;
			line-height: 34px;
			letter-spacing: -0.5px;
		}
	}

	.questions {
		gap: 36px;
	}

	.field {
		gap: 12px;

		&__label {
			font-size: $text-base;
			line-height: 140%;
		}

		&__textarea {
			height: 140px;
			padding: 12px 14px;
			font-size: $text-base;
		}

		&__select {
			height: 50px;
			padding: 12px 14px;
			font-size: $text-base;
		}
	}

	.checkboxes {
		gap: 14px;
		max-width: 100%;
	}

	.checkbox {
		align-items: flex-start;

		&__label {
			font-size: $text-xs;
			line-height: 140%;
		}
	}
}
</style>
